import { motion } from "framer-motion";
import { Recycle, Factory, Palette } from "lucide-react";
import ChapterMark from "@/components/ChapterMark";

const PILLARS = [
    {
        icon: Recycle,
        value: "25% of recyclables",
        title: "Offered back to Community",
        detail: "A quarter of all recyclable material, 1,106 tonnes over ten years, is offered to Indigenous communities and artists, to repurpose and recreate as Indigenous Australian art, and to sell for profit if they choose.",
    },
    {
        icon: Factory,
        value: "75% sold on",
        title: "Recycled by Australian industry",
        detail: "The remaining recyclable material, 3,319 tonnes over ten years, is sorted and sold to Australian recyclers such as Pact Group and Sell & Parker, helping fund the fleet without donations.",
    },
    {
        icon: Palette,
        value: "$7,000+ commission",
        title: "Paid to the artist",
        detail: "In practice, every hull is designed by a local Indigenous artist from the Dharug and Eora nations of the Parramatta region, paid over $7,000 for their work. Commissioned, not copied.",
    },
];

const ARTWORKS = [
    { src: "/images/art-barramundi.jpg", caption: "Ghost-net barramundi, recovered materials as sculpture" },
    { src: "/images/art-giant-fish.jpg", caption: "An exhibition wall of ghost-net works: turtle, crab, crocodile and fish" },
    { src: "/images/art-artist-fish.jpg", caption: "Ghost-net works by Indigenous artists, Cape York, the model for river plastic" },
    { src: "/images/art-exhibition.jpg", caption: "A giant fish built from bottle caps, bottles and netting" },
    { src: "/images/art-seahorse.jpg", caption: "A seahorse woven from recovered netting" },
    { src: "/images/art-squids.jpg", caption: "Ghost-net squid and turtle works, community art from recovered nets" },
    { src: "/images/art-turtle.jpg", caption: "A sea turtle woven from ghost nets, Cape York" },
    { src: "/images/art-echidna.jpg", caption: "A ghost-net echidna, Country's animals remade from its pollution" },
];

export default function Repurpose() {
    return (
        <section id="repurpose" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32 bg-[#041222] border-y border-[#12283C]" data-testid="repurpose-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: "url(/images/art-echidna.jpg)" }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#041222] via-transparent to-[#041222] pointer-events-none" aria-hidden="true" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="07" label="Closing the Loop" tone="emerald" testid="repurpose-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="repurpose-heading">
                        Where the waste goes, back to Community
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="repurpose-intro">
                        The river gives up its plastic; Community turns it back into culture. This is who the
                        catch goes back to, and on what terms.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="repurpose-pillars">
                    {PILLARS.map((pillar, i) => {
                        const Icon = pillar.icon;
                        return (
                            <motion.div
                                key={pillar.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="bg-[#06182A] border border-[#12283C] rounded-xl p-6 hover:-translate-y-1 hover:border-emerald-400/40 transition-transform"
                                data-testid={`repurpose-card-${i}`}
                            >
                                <span className="w-11 h-11 rounded-lg bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-emerald-300" />
                                </span>
                                <p className="mt-4 font-display font-semibold text-3xl text-emerald-300">{pillar.value}</p>
                                <p className="mt-1 text-base font-semibold text-slate-100">{pillar.title}</p>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{pillar.detail}</p>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5 }}
                    className="mt-6 bg-[#05251C] border border-emerald-900/60 rounded-xl p-7"
                    data-testid="repurpose-intention"
                >
                    <p className="text-sm font-semibold tracking-[0.25em] uppercase text-emerald-300">Our intention</p>
                    <p className="mt-3 text-base md:text-lg text-slate-200 leading-relaxed max-w-4xl">
                        The sketches and renders on this page are prototypes and concepts only. In practice,
                        every hull is designed by a local Indigenous community member, paid a commission of over
                        $7,000. Our intention is not to culturally appropriate or profit from their art. It is to
                        recognise and showcase Indigenous talent, and the historical artistic beauty of the
                        cultures of this waterway.
                    </p>
                </motion.div>

                <div className="mt-10" data-testid="repurpose-gallery">
                    <p className="text-sm font-semibold tracking-[0.25em] uppercase text-slate-500 mb-4">
                        The tradition the catch will help fund
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {ARTWORKS.map((art, i) => (
                            <motion.div
                                key={art.src}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.45, delay: i * 0.06 }}
                                className="relative rounded-xl overflow-hidden border border-[#12283C] group bg-white/5"
                                data-testid={`repurpose-art-${i}`}
                            >
                                <img
                                    src={art.src}
                                    alt={art.caption}
                                    className="w-full h-[260px] object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-transparent to-transparent" />
                                <div className="absolute bottom-0 p-4">
                                    <p className="text-sm text-slate-200 leading-snug">{art.caption}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    <p className="mt-3 text-sm text-slate-500" data-testid="repurpose-gallery-note">
                        Reference works: ghost-net and fibre art by Indigenous artists, the practice river
                        plastic helps fund and supply.
                    </p>
                </div>
            </div>
        </section>
    );
}
